import { useEffect, useRef } from "react";

import { getChatWidgetRouteType } from "./utils";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";

const FORETHOUGHT_WIDGET_OPENED_EVENT = "forethoughtWidgetOpened";
const FORETHOUGHT_IFRAME_SRC_FRAGMENT = "forethought";

type ForethoughtMessageEvent = MessageEvent<{ event?: string }>;

const isForethoughtIframeFocused = (): boolean => {
  const activeElement = document.activeElement;
  return activeElement instanceof HTMLIFrameElement && activeElement.src.includes(FORETHOUGHT_IFRAME_SRC_FRAGMENT);
};

export const useLogForethoughtWidgetOpened = ({ enabled, path }: { enabled: boolean; path: string }) => {
  const { logAmplitudeEvent } = useAmplitude();
  const hasReceivedWidgetMessage = useRef(false);
  const isIframeFocused = useRef(false);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return;
    }

    const routeType = getChatWidgetRouteType(path);
    const logOpened = (source: "postMessage" | "iframeFocus") => {
      logAmplitudeEvent(AmplitudeEvents.ForethoughtWidgetOpened, { routeType, path, source });
    };

    const onMessage = (event: ForethoughtMessageEvent) => {
      if (event.data?.event !== FORETHOUGHT_WIDGET_OPENED_EVENT) {
        return;
      }
      const alreadyLoggedFromFocus = !hasReceivedWidgetMessage.current && isIframeFocused.current;
      hasReceivedWidgetMessage.current = true;
      if (!alreadyLoggedFromFocus) {
        logOpened("postMessage");
      }
    };

    const onWindowBlur = () => {
      if (hasReceivedWidgetMessage.current || isIframeFocused.current || !isForethoughtIframeFocused()) {
        return;
      }
      isIframeFocused.current = true;
      logOpened("iframeFocus");
    };

    const onWindowFocus = () => {
      isIframeFocused.current = false;
    };

    window.addEventListener("message", onMessage);
    window.addEventListener("blur", onWindowBlur);
    window.addEventListener("focus", onWindowFocus);

    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("blur", onWindowBlur);
      window.removeEventListener("focus", onWindowFocus);
    };
  }, [enabled, logAmplitudeEvent, path]);
};
